import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPage = () => {
  const lastUpdate = "14 de marzo de 2026";

  return (
    <div className="min-h-screen bg-audioyou-bg">
      {/* Header */}
      <header className="neumorphic-header sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-audioyou-accent flex items-center justify-center">
                <span className="text-white text-xl">🎙️</span>
              </div>
              <span className="text-xl font-bold text-gray-800">AudioToo</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/" className="neumorphic-button-small inline-flex items-center gap-2 px-3 py-2 mb-6">
          <ArrowLeft size={16} />
          <span>Volver</span>
        </Link>

        <div className="neumorphic-card p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Política de Privacidad</h1>
          <p className="text-sm text-gray-500 mb-8">Última actualización: {lastUpdate}</p>

          <div className="prose prose-gray max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">1. Información que Recopilamos</h2>
              <p className="text-gray-600 leading-relaxed mb-3">
                Recopilamos los siguientes tipos de información:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li><strong>Información de cuenta:</strong> Nombre, email y foto de perfil (obtenidos de Google al iniciar sesión).</li>
                <li><strong>Contenido:</strong> Audios que grabas y subes, incluyendo metadatos (duración, clasificación visual).</li>
                <li><strong>Uso:</strong> Interacciones con la app (reproducciones, likes, guardados, compartidos).</li>
                <li><strong>Dispositivo:</strong> Tipo de navegador, sistema operativo, dirección IP.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">2. Cómo Usamos tu Información</h2>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Proporcionar y mejorar nuestros servicios.</li>
                <li>Personalizar tu experiencia en la plataforma.</li>
                <li>Mostrar contenido relevante en tu feed.</li>
                <li>Comunicarnos contigo sobre tu cuenta o cambios en el servicio.</li>
                <li>Detectar y prevenir fraude o uso indebido.</li>
                <li>Cumplir con obligaciones legales.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">3. Compartición de Información</h2>
              <p className="text-gray-600 leading-relaxed mb-3">
                No vendemos tu información personal. Podemos compartirla en estos casos:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li><strong>Contenido público:</strong> Los audios que publicas son visibles para otros usuarios.</li>
                <li><strong>Proveedores de servicios:</strong> Terceros que nos ayudan a operar la plataforma (hosting, análisis).</li>
                <li><strong>Requisitos legales:</strong> Cuando la ley lo requiera o para proteger nuestros derechos.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">4. Almacenamiento y Seguridad</h2>
              <p className="text-gray-600 leading-relaxed">
                Tu información se almacena en servidores seguros. Implementamos medidas de seguridad 
                técnicas y organizativas para proteger tus datos contra acceso no autorizado, pérdida 
                o alteración. Sin embargo, ningún sistema es 100% seguro.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">5. Tus Derechos</h2>
              <p className="text-gray-600 leading-relaxed mb-3">
                Tienes derecho a:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li><strong>Acceder:</strong> Solicitar una copia de tus datos personales.</li>
                <li><strong>Rectificar:</strong> Corregir información inexacta.</li>
                <li><strong>Eliminar:</strong> Solicitar la eliminación de tu cuenta y datos.</li>
                <li><strong>Portabilidad:</strong> Recibir tus datos en un formato estructurado.</li>
                <li><strong>Oposición:</strong> Oponerte al procesamiento de tus datos.</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-3">
                Para ejercer estos derechos, contáctanos en{' '}
                <a href="mailto:privacy@audioyou.app" className="text-audioyou-accent hover:underline">
                  privacy@audioyou.app
                </a>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">6. Cookies</h2>
              <p className="text-gray-600 leading-relaxed">
                Usamos cookies esenciales para el funcionamiento de la plataforma, como mantener tu 
                sesión iniciada. Puedes configurar tu navegador para rechazar cookies, pero esto 
                puede afectar la funcionalidad del servicio.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">7. Retención de Datos</h2>
              <p className="text-gray-600 leading-relaxed">
                Conservamos tu información mientras mantengas una cuenta activa. Al eliminar tu cuenta, 
                eliminaremos tus datos personales en un plazo de 30 días, excepto cuando la ley requiera 
                su conservación.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">8. Menores de Edad</h2>
              <p className="text-gray-600 leading-relaxed">
                AudioToo no está dirigido a menores de 13 años. No recopilamos intencionadamente 
                información de menores de 13 años. Si descubrimos que hemos recopilado datos de un 
                menor, los eliminaremos inmediatamente.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">9. Cambios en esta Política</h2>
              <p className="text-gray-600 leading-relaxed">
                Podemos actualizar esta política ocasionalmente. Te notificaremos sobre cambios 
                significativos mediante un aviso en la plataforma o por email.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">10. Contacto</h2>
              <p className="text-gray-600 leading-relaxed">
                Para preguntas sobre esta Política de Privacidad:{' '}
                <a href="mailto:privacy@audioyou.app" className="text-audioyou-accent hover:underline">
                  privacy@audioyou.app
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
